/**
 * AgentModelSelect — the shared agent + model dropdown.
 *
 * This is the ONE picker for choosing an `<agent>:<model>` combination
 * (e.g. `claude:opus-4.8`). It used to live inline in the marketplace
 * workflow deploy footer; pulling it into an atom means any surface that
 * needs "pick the agent + model" renders the SAME control with the same
 * placeholder + option + title behaviour, and can't drift.
 *
 * What it owns (the genuinely shared logic):
 *   - the "Pick an agent…" placeholder option, shown only while
 *     `required` is set AND no value is chosen, with an empty value so
 *     the surrounding form stays invalid until the user picks;
 *   - mapping `options` ({ value, label }[]) to <option>s;
 *   - the `title` attribute echoing the current label (so a collapsed
 *     native select still shows the full choice on hover);
 *   - the `is-required` invalid tint toggle and aria wiring.
 *
 * What it does NOT own: surface styling. Like LoadingButton, this is a
 * transparent passthrough — the caller supplies the look via `className`
 * (the marketplace footer passes `mkt-field mkt-field--select`). We ship
 * no colours / widths from here so each host keeps its own layout.
 *
 * The option list itself (and its ORDER) is the caller's concern — it
 * comes from `useAgentOptions()` (live `/models`, newest-first per vendor)
 * with the hardcoded fallback. This atom renders whatever order it's given.
 */

import React from 'react';
import PropTypes from 'prop-types';

const AgentModelSelect = ({
  options,
  value,
  onChange,
  required = false,
  invalid = false,
  disabled = false,
  placeholder = 'Pick an agent…',
  className = '',
  ariaLabel = 'AI agent',
  id,
}) => {
  const selectClassName = [className, invalid && 'is-required']
    .filter(Boolean)
    .join(' ');

  const currentLabel =
    options.find((o) => o.value === value)?.label || placeholder;

  return (
    <select
      id={id}
      className={selectClassName}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-invalid={invalid || (required && !value)}
      title={currentLabel}
    >
      {required && (
        // Placeholder shown only when there's no default AND the user
        // hasn't picked. Empty value keeps the form invalid (the host's
        // submit stays gated until they pick a real option).
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((opt) => (
        <option key={opt.value || 'default'} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
};

AgentModelSelect.propTypes = {
  /** [{ value: '<agent>:<model>', label: 'Vendor · Model' }] — already ordered. */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  /** Current `<agent>:<model>` value, or '' for none. */
  value: PropTypes.string,
  /** Called with the raw selected value string. */
  onChange: PropTypes.func.isRequired,
  /** Render the "Pick an agent…" placeholder + gate the form when empty. */
  required: PropTypes.bool,
  /** Apply the `is-required` invalid tint (e.g. after a failed submit). */
  invalid: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  /** Surface styling — caller owns the look (e.g. 'mkt-field mkt-field--select'). */
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  id: PropTypes.string,
};

export default AgentModelSelect;
